import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteAccount, fetchMe, signOut, type Me, type Org } from "./api";

export const sessionKey = ["me"] as const;

/**
 * useSession returns the signed-in user, or null.
 *
 * It is one query shared by the whole app so that signing out invalidates every
 * view at once, rather than leaving a stale header above a 401'd page.
 */
export function useSession() {
  const query = useQuery({
    queryKey: sessionKey,
    queryFn: fetchMe,
    // The session outlives a page view; refetching it on every mount makes the
    // header flicker for no new information.
    staleTime: 5 * 60_000,
  });
  return {
    me: query.data ?? null,
    isPending: query.isPending,
    isSignedIn: !!query.data,
  };
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: signOut,
    onSuccess: async () => {
      // Everything on screen was fetched as somebody; none of it survives.
      client.setQueryData(sessionKey, null);
      await client.invalidateQueries();
    },
  });
}

/**
 * useDeleteAccount deletes the account, then forgets everything fetched as it
 * — the same clean slate as signing out, because the server has already
 * cleared the cookies.
 */
export function useDeleteAccount(onDeleted: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: async () => {
      // Before the session is cleared: clearing it unmounts the control that
      // asked, and a callback passed to `mutate` would then never run.
      onDeleted();
      client.setQueryData(sessionKey, null);
      await client.invalidateQueries();
    },
  });
}

/**
 * primaryOrg is the org a creator works in by default: their personal one.
 * Everything else they belong to was an invitation.
 */
export function primaryOrg(me: Me | null): Org | undefined {
  if (!me) return undefined;
  return me.orgs.find((o) => o.personal) ?? me.orgs[0];
}
